import React, { useEffect, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import {
  Button,
  Modal,
  Select,
  Space,
  Alert,
  FloatButton,
  notification,
} from 'antd';
import {
  PlusIcon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'store';
import {
  loadDashboardConfig,
  saveDashboardConfig,
  resetDashboardConfig,
  addCard,
  updateCardPosition,
  selectDashboardConfig,
} from 'store/slices/dashboard.slice';
import { selectCurrentUser } from 'store/slices/auth.slice';
import {
  fetchApplications,
} from 'store/slices/applications.slice';
import DashboardCard from '../../components/dashboard/DashboardCard';
import { DashboardCardConfig } from 'types/dashboard.types';
import { generateId } from '../../utils/uuid';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import '../../components/dashboard/CustomizableDashboard.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

type HeroIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const HIcon: React.FC<{ icon: HeroIcon; size?: number; style?: React.CSSProperties }> = ({
  icon: Icon,
  size = 18,
  style,
}) => <Icon style={{ width: size, height: size, ...style }} aria-hidden="true" />;

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const config = useAppSelector(selectDashboardConfig);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newCardType, setNewCardType] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    dispatch(fetchApplications({ limit: 100, offset: 0 }));
  }, [dispatch]);

  useEffect(() => {
    if (currentUser?.id) {
      dispatch(loadDashboardConfig(currentUser.id));
    }
  }, [currentUser, dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (hasUnsavedChanges && config && currentUser?.id) {
        handleSave(true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [hasUnsavedChanges, config, currentUser]);

  const handleLayoutChange = (layout: any[]) => {
    if (!config) return;

    layout.forEach((item: any) => {
      dispatch(updateCardPosition({
        cardId: item.i,
        position: {
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
        },
      }));
    });
    setHasUnsavedChanges(true);
  };

  const handleAddCard = () => {
    if (!newCardType || !config) return;

    const newCard: DashboardCardConfig = {
      id: generateId(),
      type: newCardType as DashboardCardConfig['type'],
      appId: null,
      position: {
        x: (config.cards.length * 3) % 12,
        y: Infinity,
        w: 6,
        h: 4,
      },
    };

    dispatch(addCard(newCard));
    setIsAddModalOpen(false);
    setNewCardType('');
    setHasUnsavedChanges(true);

    notification.success({
      message: 'Карточка добавлена',
      description: 'Не забудьте выбрать приложение для отображения данных',
      placement: 'bottomRight',
    });
  };

  const handleSave = async (silent = false) => {
    if (!config || !currentUser?.id) return;

    try {
      await dispatch(saveDashboardConfig({
        ...config,
        userId: currentUser.id,
      })).unwrap();

      setHasUnsavedChanges(false);

      if (!silent) {
        notification.success({
          message: 'Настройки сохранены',
          description: 'Конфигурация дашборда успешно сохранена',
          placement: 'bottomRight',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Ошибка сохранения',
        description: 'Не удалось сохранить конфигурацию',
        placement: 'bottomRight',
      });
    }
  };

  const handleReset = async () => {
    if (!currentUser?.id) return;

    try {
      await dispatch(resetDashboardConfig(currentUser.id)).unwrap();
      setHasUnsavedChanges(false);

      notification.success({
        message: 'Настройки сброшены',
        description: 'Дашборд вернулся к изначальной конфигурации',
        placement: 'bottomRight',
      });
    } catch (error) {
      notification.error({
        message: 'Ошибка сброса',
        description: 'Не удалось сбросить конфигурацию',
        placement: 'bottomRight',
      });
    }
  };

  const cardTypes = [
    { value: 'push_stats', label: ' Push-статистика' },
    { value: 'user_stats', label: ' Статистика пользователей' },
    { value: 'ctr', label: ' CTR метрики' },
    { value: 'app_stats', label: ' Статистика приложения' },
    { value: 'conversions', label: ' Конверсии' },
  ];

  if (!config || !currentUser) {
    return (
      <div className="dashboard-loading">
        Загрузка конфигурации дашборда...
      </div>
    );
  }

  const layouts = {
    lg: config.cards.map((card: DashboardCardConfig) => ({
      i: card.id,
      x: card.position.x,
      y: card.position.y,
      w: card.position.w,
      h: card.position.h,
      minW: 3,
      minH: 3,
      maxW: 12,
    })),
    md: config.cards.map((card: DashboardCardConfig) => ({
      i: card.id,
      x: card.position.x % 10,
      y: card.position.y,
      w: Math.min(card.position.w, 10),
      h: card.position.h,
      minW: 3,
      minH: 3,
    })),
    sm: config.cards.map((card: DashboardCardConfig) => ({
      i: card.id,
      x: 0,
      y: card.position.y,
      w: 6,
      h: card.position.h,
      minW: 3,
      minH: 3,
    })),
    xs: config.cards.map((card: DashboardCardConfig) => ({
      i: card.id,
      x: 0,
      y: card.position.y,
      w: 4,
      h: card.position.h,
      minW: 4,
      minH: 3,
    })),
  };

  return (
    <div className="customizable-dashboard">
      <div className="dashboard-header">
        <h1>Настраиваемая панель управления</h1>
        <p>Добавьте до 8 карточек и настройте их расположение под себя</p>
      </div>

      {hasUnsavedChanges && (
        <Alert
          message="У вас есть несохраненные изменения"
          type="warning"
          showIcon
          closable
          action={
            <Button size="small" type="primary" onClick={() => handleSave()}>
              Сохранить
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {config.cards.length === 0 ? (
        <Alert
          message="Дашборд пуст"
          description="Добавьте карточки для отображения статистики"
          type="info"
          showIcon
          action={
            <Button
              type="primary"
              icon={<HIcon icon={PlusIcon} size={16} />}
              onClick={() => setIsAddModalOpen(true)}
            >
              Добавить первую карточку
            </Button>
          }
        />
      ) : (
        <ResponsiveGridLayout
          className="layout"
          layouts={layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4 }}
          rowHeight={60}
          onLayoutChange={handleLayoutChange}
          draggableHandle=".drag-handle"
          compactType="vertical"
          preventCollision={false}
        >
          {config.cards.map((card: DashboardCardConfig) => (
            <div key={card.id}>
              <DashboardCard
                config={card}
                onRemove={() => setHasUnsavedChanges(true)}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      )}

      <FloatButton.Group
        trigger="click"
        type="primary"
        style={{ right: 24 }}
        icon={<HIcon icon={Cog6ToothIcon} size={18} />}
      >
        <FloatButton
          icon={<HIcon icon={PlusIcon} size={18} />}
          tooltip={`Добавить карточку (${config.cards.length}/8)`}
          onClick={() => setIsAddModalOpen(true)}
          disabled={config.cards.length >= 8}
        />
        <FloatButton
          icon={<HIcon icon={CloudArrowUpIcon} size={18} />}
          tooltip="Сохранить изменения"
          onClick={() => handleSave()}
          disabled={!hasUnsavedChanges}
        />
        <FloatButton
          icon={<HIcon icon={ArrowPathIcon} size={18} />}
          tooltip="Сбросить настройки"
          onClick={() => {
            Modal.confirm({
              title: 'Сбросить настройки?',
              content: 'Все карточки вернутся к изначальному состоянию',
              okText: 'Сбросить',
              cancelText: 'Отмена',
              okType: 'danger',
              onOk: handleReset,
            });
          }}
        />
      </FloatButton.Group>

      <Modal
        title="Добавить карточку"
        open={isAddModalOpen}
        onOk={handleAddCard}
        onCancel={() => {
          setIsAddModalOpen(false);
          setNewCardType('');
        }}
        okText="Добавить"
        cancelText="Отмена"
        okButtonProps={{ disabled: !newCardType }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>Выберите тип карточки:</div>
          <Select
            placeholder="Тип карточки"
            style={{ width: '100%' }}
            value={newCardType || undefined}
            onChange={setNewCardType}
            options={cardTypes}
          />
          <Alert
            message={`Можно добавить еще ${8 - config.cards.length} карточек`}
            type="info"
            showIcon
          />
        </Space>
      </Modal>
    </div>
  );
};

export default Dashboard;